/**
 * MultiSelect Component
 *
 * UI/Aesthetics: Multi-select dropdown with chips, search, and select all functionality
 * Functions:
 * - Multiple option selection with visual chips display
 * - Search/filter functionality within dropdown
 * - Select All / Deselect All toggle
 * - Alphabetical sorting of options and selected chips
 * - Configurable max chips display with overflow indicator
 * - Chip removal with delete functionality
 * - Keyboard navigation and accessibility
 *
 * The dropdown is the `@erp/ui` Combobox; this keeps MultiSelect's props and
 * semantics (pending picks with `liveUpdate={false}`, label/id search with
 * exact-id-first, grouped sections, custom values) on top of it. Behaviour is
 * pinned by `MultiSelect.behaviour.test.tsx`, written against the previous
 * hand-rolled popover.
 */

import React, { useState } from 'react';
import { T, Var } from 'gt-react';
import { Combobox, type ComboboxLabels } from '@erp/ui/combobox';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

export interface MultiSelectOption {
  id: string;
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value?: any;
  group?: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: MultiSelectOption[];
  onChange: (selected: MultiSelectOption[]) => void;
  label?: string;
  labelPosition?: 'above' | 'left'; // Position of the label relative to the dropdown
  placeholder?: string;
  searchPlaceholder?: string;
  maxChips?: number;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default' | 'small'; // 'small' for MUI compatibility
  error?: boolean;
  helperText?: string;
  className?: string;
  hideChips?: boolean;
  hideChipsWhenAllSelected?: boolean;
  renderSelectedDisplay?: (
    value: MultiSelectOption[],
    totalOptionsCount: number,
  ) => React.ReactNode | null;
  renderOptionLabel?: (option: MultiSelectOption) => React.ReactNode;
  /** Replaces the trigger text, for filters that name themselves. */
  summaryText?: (
    value: MultiSelectOption[],
    totalOptionsCount: number,
  ) => string;
  shouldAutoSort?: boolean;
  liveUpdate?: boolean;
  /**
   * When true, selected text is prefixed with the placeholder label.
   * Example: "Supplier: ABC, DEF" / "Supplier: 3 selected"
   */
  showPlaceholderInSelectionText?: boolean;
  /** Allow typing custom values not in the options list. Press Enter to add. */
  allowCustomValues?: boolean;
  /** Called when the search input changes. When provided, client-side filtering is skipped (the caller supplies pre-filtered options). */
  onSearchChange?: (search: string) => void;
  /** The trigger's accessible name, when no visible `label` names it. */
  'aria-label'?: string;
  /**
   * List the current selection first, above the search results, whatever the
   * search — so a server-searched picker keeps earlier picks visible (and
   * untickable) after the next search replaces `options`.
   */
  pinSelected?: boolean;
  /** Show the Select All / Deselect All row. Defaults to true. */
  showSelectAll?: boolean;
}

/**
 * Filter options by search term (from the sorted list), matching label or id;
 * exact id matches sort to the top. An empty term keeps every option — the
 * caller passes '' when it searches server-side (`onSearchChange`).
 */
function searchOptions(
  options: MultiSelectOption[],
  searchTerm: string,
): MultiSelectOption[] {
  if (!searchTerm) return options;
  const term = searchTerm.toLowerCase();
  return options
    .filter(
      (option) =>
        option.label.toLowerCase().includes(term) ||
        option.id.toLowerCase().includes(term),
    )
    .sort((a, b) => {
      const aId = a.id.toLowerCase() === term ? 0 : 1;
      const bId = b.id.toLowerCase() === term ? 0 : 1;
      return aId - bId;
    });
}

/** What is ticked right now: the pending picks while an apply-on-close popover is open. */
function selection(
  open: boolean,
  liveUpdate: boolean,
  pendingValue: MultiSelectOption[],
  value: MultiSelectOption[],
): MultiSelectOption[] {
  return open && !liveUpdate ? pendingValue : value;
}

function sortByLabelIf(
  shouldSort: boolean,
  options: MultiSelectOption[],
): MultiSelectOption[] {
  if (!shouldSort) return options;
  return [...options].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
  );
}

/** The trigger's text for the current selection. */
function multiSelectButtonText(args: {
  summaryText?: (
    value: MultiSelectOption[],
    totalOptionsCount: number,
  ) => string;
  showPlaceholderInSelectionText: boolean;
  value: MultiSelectOption[];
  sortedSelectedByLabel: MultiSelectOption[];
  placeholder: string;
  optionCount: number;
}): string {
  const { value, placeholder, optionCount } = args;
  if (args.summaryText) {
    return args.summaryText(args.sortedSelectedByLabel, optionCount);
  }
  if (args.showPlaceholderInSelectionText) {
    if (value.length === 0) return placeholder;
    if (value.length <= 2) {
      const labels = args.sortedSelectedByLabel.map((v) => v.label).join(', ');
      return `${placeholder}: ${labels}`;
    }
    return `${placeholder}: ${value.length} selected`;
  }
  if (value.length === 0) return placeholder;
  if (value.length === 1) return value[0].label;
  return `${value.length}/${optionCount} selected`;
}

/**
 * Whether the search term can be added as a custom value: not blank, not an
 * existing option's label, and not already selected.
 */
function canAddCustomValue(
  allowCustomValues: boolean,
  searchTerm: string,
  options: MultiSelectOption[],
  selected: MultiSelectOption[],
): boolean {
  const term = searchTerm.trim().toLowerCase();
  if (!allowCustomValues || !term) return false;
  const matchesExisting = options.some((o) => o.label.toLowerCase() === term);
  const alreadySelected = selected.some((v) => v.id.toLowerCase() === term);
  return !matchesExisting && !alreadySelected;
}

type OptionSection = { label?: string; items: MultiSelectOption[] };

/** Options by group, single-member groups flattened, ungrouped last. */
function groupOptions(options: MultiSelectOption[]): OptionSection[] {
  const groups = new Map<string, MultiSelectOption[]>();
  const ungrouped: MultiSelectOption[] = [];
  for (const opt of options) {
    if (opt.group) {
      const arr = groups.get(opt.group) ?? [];
      arr.push(opt);
      groups.set(opt.group, arr);
    } else {
      ungrouped.push(opt);
    }
  }
  const flattenedGroups: OptionSection[] = [];
  for (const [group, opts] of groups.entries()) {
    flattenedGroups.push(
      opts.length === 1 ? { items: opts } : { label: group, items: opts },
    );
  }
  return [...flattenedGroups, { items: ungrouped }];
}

/**
 * The option sections to render. With `pin`, the selection is its own leading
 * section, so grouped search results can't push an earlier pick below them.
 */
function groupListedOptions(
  options: MultiSelectOption[],
  pin: boolean,
  selected: MultiSelectOption[],
): OptionSection[] {
  if (!pin || selected.length === 0) return groupOptions(options);
  const selectedIds = new Set(selected.map((option) => option.id));
  return [
    { items: [...selected] },
    ...groupOptions(options.filter((option) => !selectedIds.has(option.id))),
  ];
}

/** Every option is in the selection (for hideChipsWhenAllSelected). */
function everyOptionSelected(
  options: MultiSelectOption[],
  value: MultiSelectOption[],
): boolean {
  if (options.length === 0) return false;
  return (
    value.length === options.length &&
    options.every((option) => value.some((v) => v.id === option.id))
  );
}

const NARROW_POPOVER_WIDTH = '200px';
const WIDE_POPOVER_WIDTH = '300px';

function multiSelectTriggerClassName({
  compact,
  hasValue,
  fullWidth,
  error,
  className,
}: {
  compact: boolean;
  hasValue: boolean;
  fullWidth: boolean;
  error?: boolean;
  className?: string;
}): string {
  return cn(
    'justify-between font-normal',
    compact && 'h-8 gap-1.5 px-3',
    hasValue ? 'border-foreground' : 'text-muted-foreground',
    fullWidth ? 'w-full' : 'w-auto',
    error && 'border-destructive bg-destructive/5 ring-1 ring-destructive/20',
    className,
  );
}

function isCatchAll(option: MultiSelectOption): boolean {
  const value: unknown = option.value;
  return (
    typeof value === 'object' &&
    value !== null &&
    'isCatchAll' in value &&
    value.isCatchAll === true
  );
}

/** The old popover's strings, so their existing translations still apply. */
function multiSelectLabels(
  buttonText: string,
  searchPlaceholder: string,
): Partial<ComboboxLabels> {
  return {
    placeholder: buttonText,
    search: searchPlaceholder,
    empty: <T context="multiselect empty state">No options found</T>,
    selectAll: <T context="multiselect toggle all">Select All</T>,
    deselectAll: <T context="multiselect toggle all">Deselect All</T>,
    create: (term) => (
      <T context="multiselect add custom value">
        Add &ldquo;<Var>{term}</Var>&rdquo;
      </T>
    ),
  };
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  label,
  labelPosition = 'above',
  placeholder = 'Select options...',
  searchPlaceholder = 'Search',
  maxChips = 4,
  disabled = false,
  fullWidth = false,
  size = 'default',
  error = false,
  helperText,
  className,
  hideChips = true,
  hideChipsWhenAllSelected = true,
  renderSelectedDisplay,
  summaryText,
  renderOptionLabel,
  shouldAutoSort = true,
  liveUpdate = true,
  showPlaceholderInSelectionText = false,
  allowCustomValues = false,
  onSearchChange,
  'aria-label': ariaLabel,
  pinSelected = false,
  showSelectAll = true,
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTermRaw] = useState('');
  const setSearchTerm = (term: string) => {
    setSearchTermRaw(term);
    onSearchChange?.(term);
  };
  // Pending value that tracks selections while popover is open
  const [pendingValue, setPendingValue] = useState<MultiSelectOption[]>(value);

  // Sync pendingValue with value when the popover opens — only then. While
  // it is open the pending picks are the user's; re-syncing on every new
  // `value` identity (a parent re-render, e.g. a server search) dropped them.
  // Apply pending changes when popover closes
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen && !open) {
      setPendingValue(value);
    }
    if (!newOpen && open && !liveUpdate) {
      onChange(pendingValue);
    }
    setSearchTerm('');
    setOpen(newOpen);
  };

  const updateSelection = (newValue: MultiSelectOption[]) => {
    setPendingValue(newValue);
    if (liveUpdate) {
      onChange(newValue); // propagate immediately if live
    }
  };

  const compact = size === 'small' || size === 'sm';
  const triggerClassName = multiSelectTriggerClassName({
    compact,
    hasValue: value.length > 0,
    fullWidth,
    error,
    className,
  });

  // Sort options alphabetically for consistent dropdown ordering
  const sortedOptions = sortByLabelIf(shouldAutoSort, options);

  // Filter options based on search term (from the sorted list).
  // When onSearchChange is provided, the caller supplies pre-filtered options.
  // Matches against both label and id; exact id matches sort to the top.
  const searchedOptions = searchOptions(
    sortedOptions,
    onSearchChange ? '' : searchTerm,
  );

  const currentSelection = selection(open, liveUpdate, pendingValue, value);

  // Sort selected chips alphabetically by label for display (only if shouldAutoSort is true)
  const sortedSelectedByLabel = sortByLabelIf(shouldAutoSort, value);

  const handleAddCustomValue = (term: string) => {
    const customOption: MultiSelectOption = { id: term, label: term };
    updateSelection([...currentSelection, customOption]);
  };

  // Grouped options logic
  const groupedOptions = groupListedOptions(
    searchedOptions,
    pinSelected,
    currentSelection,
  );

  // Check if all options are selected (for hideChipsWhenAllSelected)
  const allOptionsSelected = everyOptionSelected(options, value);

  // Render selected chips (alphabetically sorted by label)
  const renderChips = () => {
    const visibleChips = sortedSelectedByLabel.slice(0, maxChips);
    const remainingChips = sortedSelectedByLabel.slice(maxChips);
    const remainingCount = value.length - maxChips;

    return (
      <div
        className={cn('flex flex-wrap gap-1', value.length > 0 ? 'mb-2' : '')}
      >
        {visibleChips.map((option) => (
          <Tooltip key={option.id}>
            <TooltipTrigger asChild>
              <div>
                <Badge
                  variant="outline"
                  className="text-xs px-2 py-1 bg-transparent border-border text-foreground"
                >
                  {option.label.length > 15
                    ? option.label.slice(0, 15) + '...'
                    : option.label}
                </Badge>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="whitespace-pre-line">{option.label}</p>
            </TooltipContent>
          </Tooltip>
        ))}
        {remainingCount > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Badge
                  variant="outline"
                  className="text-xs px-2 py-1 bg-muted text-muted-foreground border-border"
                >
                  <T context="multiselect overflow chip">
                    +<Var>{remainingCount}</Var> more
                  </T>
                </Badge>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p className="whitespace-pre-line">
                {remainingChips.map((option) => option.label).join('\n')}
              </p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    );
  };

  const buttonText = multiSelectButtonText({
    summaryText,
    showPlaceholderInSelectionText,
    value,
    sortedSelectedByLabel,
    placeholder,
    optionCount: options.length,
  });

  const labels = multiSelectLabels(buttonText, searchPlaceholder);

  const labelElement = label ? (
    <Label className={cn('text-sm font-medium', error && 'text-destructive')}>
      {label}
    </Label>
  ) : null;

  /** The trigger and its dropdown; only the wider one offers a custom value. */
  const renderPopover = (minWidth: string, offerCustom: boolean) => (
    <div className="flex">
      <Combobox<MultiSelectOption>
        groups={groupedOptions}
        getValue={(option) => option.id}
        getLabel={(option) => option.label}
        multiple
        value={currentSelection.map((option) => option.id)}
        selectedItems={currentSelection}
        onValueChange={(_ids, items) => updateSelection(items)}
        open={open}
        onOpenChange={handleOpenChange}
        search={searchTerm}
        onSearchChange={setSearchTerm}
        filter={null}
        resetSearchOnClose={false}
        selectAll={showSelectAll}
        groupToggle
        indicator="checkbox"
        highlightFirst={false}
        closeButton
        onCreate={
          allowCustomValues && offerCustom ? handleAddCustomValue : undefined
        }
        isCreatable={(term) =>
          canAddCustomValue(allowCustomValues, term, options, currentSelection)
        }
        renderItem={
          renderOptionLabel ? (option) => renderOptionLabel(option) : undefined
        }
        renderValue={() => buttonText}
        itemClassName={(option) =>
          isCatchAll(option) ? 'bg-blue-50/50' : undefined
        }
        // Never modal, as before — even inside a dialog, whose other
        // controls stay reachable while the dropdown is open.
        modal={false}
        disabled={disabled}
        aria-label={ariaLabel}
        className={triggerClassName}
        contentStyle={{ minWidth, maxWidth: 'min(80vw, 800px)' }}
        labels={labels}
      />
    </div>
  );

  const selectionFooter = (
    <>
      {/* Selected chips - always render container to prevent shifting */}
      {!hideChips && !(hideChipsWhenAllSelected && allOptionsSelected) && (
        <div
          className={cn(
            'flex flex-wrap gap-1 mb-2',
            value.length === 0 && 'opacity-0',
          )}
        >
          {renderSelectedDisplay
            ? renderSelectedDisplay(value, options.length)
            : value.length > 0 && renderChips()}
        </div>
      )}

      {helperText && (
        <p
          className={cn(
            'text-sm',
            error ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {helperText}
        </p>
      )}
    </>
  );

  return (
    <TooltipProvider>
      <div
        className={cn(labelPosition === 'above' ? 'space-y-2' : '', className)}
      >
        {labelPosition === 'above' && labelElement}

        {labelPosition === 'left' ? (
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">{labelElement}</div>
            <div className="flex-1 flex flex-col">
              {renderPopover(NARROW_POPOVER_WIDTH, false)}
              {selectionFooter}
            </div>
          </div>
        ) : (
          <>
            {renderPopover(WIDE_POPOVER_WIDTH, true)}
            {selectionFooter}
          </>
        )}
      </div>
    </TooltipProvider>
  );
};

export default MultiSelect;
